import posthog, { type PostHog } from "posthog-js/dist/module.slim";

import { posthogApiHost, posthogApiKey } from "./config";
import type { ConversionEvent } from "./types";

/**
 * PostHog plus Google Tag Manager's `dataLayer`.
 *
 * The slim build, which is the core client with no extensions: no click
 * autocapture, session recording, surveys, heatmaps or toolbar. This bundle only
 * captures named events and reads the distinct id, and `$pageview` is core, so
 * slim is half the size for nothing this needs. Anything wanted back is an
 * extension bundle from `posthog-js/dist/extension-bundles`, passed to `init`.
 *
 * The distinct id has to be supplied for server-side attribution; without it
 * the server skips every capture.
 *
 * Nothing starts on import. {@link initAnalytics} is the trigger, called from
 * `store.ts` on `alpine:init`, so a page that enqueues the script and renders no
 * components captures nothing.
 */

const DEFAULT_API_HOST = "https://eu.i.posthog.com";

/** Null until {@link initAnalytics} succeeds, and for good if no key is set. */
let client: PostHog | null = null;

/**
 * Starts PostHog, if a key is configured.
 *
 * Safe to call more than once: posthog-js treats a second `init` as a no-op,
 * and this returns before reaching it.
 */
export function initAnalytics(): void {
  if (client || !posthogApiKey) {
    return;
  }

  try {
    client =
      posthog.init(posthogApiKey, {
        api_host: posthogApiHost || DEFAULT_API_HOST,
      }) ?? null;
  } catch (e) {
    console.error("[ai-search] posthog init failed", e);
    return;
  }

  send("initialise_script", {});
}

function send(event: string, properties: Record<string, unknown>): void {
  try {
    client?.capture(event, properties);
  } catch (e) {
    console.error("[ai-search] posthog capture failed", e);
  }
}

/**
 * Sent as `x-distinct-id` on every API call. Empty when PostHog is off.
 *
 * A function rather than a value because it is read live: the client does not
 * exist yet when this module is imported, and `http.ts` calls this per request
 * for the same reason.
 */
export function distinctId(): string {
  try {
    return client?.get_distinct_id() ?? "";
  } catch {
    return "";
  }
}

export function capture(
  event: string,
  properties: Record<string, unknown> = {},
): void {
  send(event, properties);

  // https://developers.google.com/tag-platform/tag-manager/datalayer
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({ event, ...properties });
  }
}

/**
 * A click that led somewhere: a result, or a link the model wrote inline.
 *
 * Here rather than at the two call sites, so the event name is written once, in
 * the module that sends it. Renaming it, or adding a property every conversion
 * should carry, is then one edit.
 */
export function conversion(event: ConversionEvent): void {
  capture("ai_conversion", event);
}
